import React, { useState, useEffect } from 'react';

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

const setCookie = (name, value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [personalization, setPersonalization] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = getCookie('cookie_consent');
    if (!consent) {
      setVisible(true);
    }
  }, []);

  // Auto-dismiss on scroll
  useEffect(() => {
    if (!visible || showPreferences) return;
    const handleScroll = () => {
      if (window.scrollY > 100) {
        acceptAll();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visible, showPreferences]);

  const acceptAll = () => {
    setCookie('cookie_consent', JSON.stringify({ essential: true, personalization: true, analytics: true }));
    setVisible(false);
    setShowPreferences(false);
  };

  const savePreferences = () => {
    setCookie('cookie_consent', JSON.stringify({ essential: true, personalization, analytics }));
    setVisible(false);
    setShowPreferences(false);
  };

  // Called from footer link
  useEffect(() => {
    const handler = () => {
      const consent = getCookie('cookie_consent');
      if (consent) {
        try {
          const prefs = JSON.parse(consent);
          setPersonalization(prefs.personalization ?? true);
          setAnalytics(prefs.analytics ?? false);
        } catch (e) {}
      }
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9998,
        background: 'rgba(26, 26, 26, 0.95)',
        backdropFilter: 'blur(16px)',
        borderTop: '1px solid rgba(163, 163, 163, 0.1)',
        padding: '20px 24px',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div className="max-w-2xl mx-auto">
        {!showPreferences ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ fontSize: '0.88rem', fontWeight: 300, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              We use cookies to keep this page running and improve your experience. Simply continue or click 
              "noted" to acknowledge our cookie policy.
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={acceptAll}
                className="cta-pill-landing"
                style={{ fontSize: '0.75rem', padding: '10px 24px' }}
                data-testid="cookie-accept-btn"
              >
                Noted
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 300,
                  fontSize: '0.82rem',
                  color: 'var(--accent-teal)',
                  opacity: 0.7,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  padding: '8px 4px',
                }}
                data-testid="cookie-preferences-btn"
              >
                Preferences
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ fontSize: '0.88rem', fontWeight: 300, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Manage your cookie preferences:
            </p>

            {/* Essential - always on */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(163,163,163,0.08)' }}>
              <div>
                <p style={{ fontSize: '0.88rem', fontWeight: 400, color: 'var(--text-primary)', marginBottom: '2px' }}>Essential</p>
                <p style={{ fontSize: '0.78rem', fontWeight: 300, color: 'var(--text-secondary)' }}>Keeps the experience running. Cannot be disabled.</p>
              </div>
              <div style={{ 
                width: '40px', height: '22px', borderRadius: '11px', 
                background: 'var(--accent-teal)', opacity: 0.6,
                position: 'relative', flexShrink: 0, marginLeft: '16px'
              }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', right: '3px' }} />
              </div>
            </div>

            {/* Personalization */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(163,163,163,0.08)' }}>
              <div>
                <p style={{ fontSize: '0.88rem', fontWeight: 400, color: 'var(--text-primary)', marginBottom: '2px' }}>Personalization</p>
                <p style={{ fontSize: '0.78rem', fontWeight: 300, color: 'var(--text-secondary)' }}>Remembers your progress and preferences.</p>
              </div>
              <button
                onClick={() => setPersonalization(!personalization)}
                data-testid="cookie-toggle-personalization"
                style={{ 
                  width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                  background: personalization ? 'var(--accent-teal)' : 'rgba(163,163,163,0.3)',
                  opacity: personalization ? 0.8 : 1,
                  position: 'relative', flexShrink: 0, marginLeft: '16px', transition: 'background 0.2s'
                }}
              >
                <div style={{ 
                  width: '16px', height: '16px', borderRadius: '50%', background: '#fff', 
                  position: 'absolute', top: '3px',
                  left: personalization ? 'auto' : '3px',
                  right: personalization ? '3px' : 'auto',
                  transition: 'all 0.2s'
                }} />
              </button>
            </div>

            {/* Analytics */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <div>
                <p style={{ fontSize: '0.88rem', fontWeight: 400, color: 'var(--text-primary)', marginBottom: '2px' }}>Analytics</p>
                <p style={{ fontSize: '0.78rem', fontWeight: 300, color: 'var(--text-secondary)' }}>Helps us understand what's resonating. No ads.</p>
              </div>
              <button
                onClick={() => setAnalytics(!analytics)}
                data-testid="cookie-toggle-analytics"
                style={{ 
                  width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                  background: analytics ? 'var(--accent-teal)' : 'rgba(163,163,163,0.3)',
                  opacity: analytics ? 0.8 : 1,
                  position: 'relative', flexShrink: 0, marginLeft: '16px', transition: 'background 0.2s'
                }}
              >
                <div style={{ 
                  width: '16px', height: '16px', borderRadius: '50%', background: '#fff', 
                  position: 'absolute', top: '3px',
                  left: analytics ? 'auto' : '3px',
                  right: analytics ? '3px' : 'auto',
                  transition: 'all 0.2s'
                }} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                onClick={savePreferences}
                className="cta-pill-landing"
                style={{ fontSize: '0.75rem', padding: '10px 24px' }}
                data-testid="cookie-save-btn"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
